import { IS_DEMO } from '../../apiBase';

// Models offered for page categorization. Ids must stay in sync with the
// backend allowlist (blueprints/notice.py, which validates independently) and
// the admin GEMINI_PRICING table so usage cost is tracked.
export interface NoticeModel {
  id: string;
  label: string;
  // Color identifying the model in shared UI (e.g. the section-boundary lines
  // drawn on the PDF). Hex so it can feed inline canvas/SVG styles directly.
  color: string;
  // Approximate USD cost of ONE generated 1K image (image models only). The output
  // image dominates the bill; the input images + text add well under a cent, so this
  // is a fair "cost of this generation". Kept next to the per-1M pricing in the
  // backend GEMINI_PRICING table.
  pricePerImage?: number;
  // Nothing new runs on it, but documents analysed on it still do. It stays in this
  // list because the list is not only a picker: stored classifications are keyed BY
  // MODEL ID, and every display path finds them by walking these entries. Drop a
  // retired model from here and the manuals that ran on it lose their Phase 1 table,
  // their section lines on the PDF, and — through `authoritativeModel` — their steps
  // and their parts, all at once.
  retired?: boolean;
}

// THE OPENAI MODEL THIS APP PUTS A READING QUESTION TO, where Gemini is not the one asked.
// Not a choice a reader makes: each call that goes to it was decided by running the two side
// by side.
//
// Here rather than at each caller, which is where it was going: the id was written out in
// listingCropsRun and again in the label table under it, which is two copies of one string
// and one of them already had to be found by grep.
export const ASTRA_MODEL = 'gpt-6-astra';

// AND HOW HARD IT THINKS ON THEM, which is not asked either. Nothing this app puts to Astra —
// Phase 1's reads, Phase 4's two calls, Phase 2's listing crops — names a reasoning effort, so every one of them
// runs at the model's own default. That default is `medium`: see openai_blender.MODELS, where
// it is recorded as read off the API itself, and where the note beside it says the same thing
// this comment does.
//
// WHICH IS WHY IT CAN BE PRINTED BESIDE THE NAME rather than stored with each answer. The level
// has never been a choice here, so it is as true of a run from July as of the next one — a
// badge naming it is right about every answer in the library, including the ones that came back
// before anything said so.
//
// IF IT EVER STOPS BEING TRUE, the journal says so before this does: the server logs the effort
// the RESPONSE reports it ran at, on every call (see _ask_on_images). A model whose default
// moved would show there as a line that disagrees with this word.
export const ASTRA_EFFORT = 'medium';

export const NOTICE_MODELS: NoticeModel[] = [
  // PHASE 1 IS ASKED OF GPT-6 ASTRA — the sections, and the rest of its reads with them (the
  // manual's info, each listing and its pictures; those choose their model on the server, see
  // notice.INFO_MODEL). First in this list because the list's ORDER is what `authoritativeModel`
  // walks: a run classified by both reads its bands off the first.
  { id: ASTRA_MODEL, label: `GPT-6 Astra (${ASTRA_EFFORT})`, color: '#f97316' }, // orange-500
  // Retired from the SECTIONS only: every call that sends RUN_MODEL still runs on it.
  { id: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash', color: '#06b6d4', retired: true }, // cyan-500
  { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash', color: '#10b981', retired: true }, // blue-500
  { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', color: '#6366f1', retired: true }, // indigo-500
  // Gemini 3.1 Flash-Lite was retired from the picker before this flag existed and is
  // simply absent; nothing was ever run on it. Re-add any of them without `retired` to
  // bring it back to the sections — the backend still accepts them.
];

// The ones api_usage prices, which is the Gemini ones: an OpenAI call is logged to the
// journal, not to that table (see _ask_on_images), so a row for Astra there would be a $0
// that is not true.
export const PRICED_MODELS = NOTICE_MODELS.filter((m) => m.id !== ASTRA_MODEL);

// THE model every new call goes to, whatever a document was analysed on. Re-running one
// phase of an old manual — one hole search, one box redo — is new work and gets the current
// model; only the READING of stored answers stays with the model that produced them. Those
// two questions used to share one value (`bands.model`), which meant a document could never
// be worked on with anything but the model of its first run.
//
// Astra, like every other reading call: Phase 2's list, boxes, sizes, outlines and estimates
// were the last on Gemini, kept there by default rather than by any comparison.
export const RUN_MODEL = ASTRA_MODEL;

// The models a fresh SECTIONS run uses. Same list minus what is retired.
export const ACTIVE_MODELS = NOTICE_MODELS.filter((m) => !m.retired);

// AND PHASE 4 IS ONE OF THEM — both its calls, the step breakdown and the holes it fills.
// They are the hardest reading in the app: a page of diagrams, a dozen gestures, and an
// answer that has to name the right piece of the right part.
//
// Named as the PHASE's model rather than used raw at its four call sites, so moving it back
// or on is one line here and the badges cannot disagree with the buttons.
export const STEPS_MODEL = ASTRA_MODEL;

// AND PHASE 3 — the repartition and the transcription. The repartition is a judgement over
// every step's diagram at once; the transcription rode along on the same engine because one
// phase on two engines is two pricing stories for one button.
export const STEP_PARTS_MODEL = ASTRA_MODEL;

// AND PHASE 7'S ONE CALL, the movement a sub-step is. Gemini 3.7 Flash landed the spring washer
// on top of the screw's head rather than under it; it is the same kind of reading as Phase 4's,
// off the same diagrams, so it goes to the same model.
export const FITTING_MODEL = ASTRA_MODEL;

// ONE MODEL ID IN WORDS, for the badges that say what a call would go to.
//
// The id back, unchanged, for anything not in the list above — and that is the common case
// rather than a fallback: the picker holds what Phases 1-2 run on, and the calls made
// elsewhere choose their own model on the server (mesh_advice, mesh_orient). An id nobody
// wrote a label for is still the answer to "which model", and printing it is better than
// printing nothing or, worse, the wrong pretty name.
export const modelLabel = (id: string): string =>
  // IN THE DEMO, NO MODEL IS NAMED: the demo shows what the app does, not which AI does it.
  // Empty there, and every caller drops the line rather than print an empty name.
  (IS_DEMO ? '' : NOTICE_MODELS.find((m) => m.id === id)?.label || id);

// Retired from the picker, but still run server-side for the Phase 2 real-image
// filter (and kept in the backend allowlist/pricing). Exposed so the Pricing tab
// can label that filter's usage.
export const FILTER_MODEL: NoticeModel = {
  id: 'gemini-3.1-flash-lite',
  label: 'Gemini 3.1 Flash-Lite',
  color: '#f59e0b', // amber-500
};

// Phase 2's main option: generating a real photo of a part from its manual crop.
// Two Nano Banana models the user toggles between, in ascending capability/cost —
// the all-rounder default, then Pro (best, priciest). Not in the run picker (driven
// by Phase 2's own toggle); exposed so the Pricing tab can label and price their
// usage. Ids match the backend allowlist/pricing.
// The two Nano Banana model ids, named so the catalog can split its real-image
// columns (and anything else) by model without depending on array order.
export const GEN_MODEL_NB2 = 'gemini-3.1-flash-image';
export const GEN_MODEL_PRO = 'gemini-3-pro-image';

// Labelled without the "Nano", which every one of them shares and so distinguishes none
// of them. It is printed under each generated thumbnail, at a width of eighty pixels, and
// two thirds of that line was the half of the name the two models have in common.
export const GEN_MODELS: NoticeModel[] = [
  { id: GEN_MODEL_NB2, label: 'Banana 2', color: '#8b5cf6', pricePerImage: 0.067 }, // violet-500
  { id: GEN_MODEL_PRO, label: 'Banana Pro', color: '#7c3aed', pricePerImage: 0.134 }, // violet-600
];

// The default: Nano Banana 2, the general-purpose middle option.
export const DEFAULT_GEN_MODEL = GEN_MODEL_NB2;
